import * as yup from 'yup';
import { getDefaultBaseCurrency } from '../../../utils/auth.util';

const isEmptyObj = v =>
  v == null ||
  v === '' ||
  (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0);

const isEmptySelect = v =>
  v == null ||
  (typeof v === 'object' &&
    (Object.keys(v).length === 0 || (!v.value && !v.label)));

const optionObjectSchema = yup.object({
  value: yup.string().required('Required'),
  label: yup.string().required('Required'),
});

const isEmptyFlightQuote = q => {
  if (!q) return true;
  const {
    fromCity,
    fromCityName,
    toCountry,
    toCountryName,
    toCity,
    date,
    deptTime,
    airline,
    airlineName,
    class: cls,
    traveller,
  } = q;

  return (
    isEmptySelect(fromCity) &&
    isEmptyObj(fromCityName) &&
    isEmptySelect(toCountry) &&
    isEmptyObj(toCountryName) &&
    isEmptySelect(toCity) &&
    isEmptyObj(date) &&
    isEmptyObj(deptTime) &&
    isEmptySelect(airline) &&
    isEmptyObj(airlineName) &&
    isEmptyObj(cls) &&
    isEmptyObj(traveller)
  );
};

const isFlightOptionInactive = opt =>
  Array.isArray(opt?.quotes) && opt.quotes.every(isEmptyFlightQuote);

// To coerce text inputs like "" to null/number
const numTransform = (v, orig) => {
  if (typeof orig === 'string' && orig.trim() === '') {
    return null;
  }
  if (typeof orig === 'string') {
    return Number(orig);
  }
  return v;
};

const quoteSchema = yup.object({
  fromCity: optionObjectSchema.required('From city is required'),
  fromCityName: yup.string().required('From city name is required'),
  toCountry: optionObjectSchema.required('To country is required'),
  toCountryName: yup.string().required('To country name is required'),
  toCity: optionObjectSchema.required('To city is required'),
  date: yup
    .string()
    .required('Date is required')
    .test('not-in-past', 'Date cannot be in the past', value => {
      if (!value) return true;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return new Date(value) >= today;
    }),
  deptTime: yup.string().required('Dept. time is required'),
  airline: optionObjectSchema.required('Airline is required'),
  airlineName: yup.string().required('Airline name is required'),
  class: yup.string().required('Class is required'),
  traveller: yup.string().optional(),
});

const strictFlightOptionSchema = yup.object({
  quotes: yup
    .array()
    .of(quoteSchema)
    .min(1, 'At least one quotation is required')
    .required(),
  agency: yup.string().required('Travel Agency is required'),
  currency: yup.string().required('Currency is required'),
  terms: yup.string().optional(),
  quoteValue: yup
    .number()
    .transform(numTransform)
    .typeError('Quote value must be a number')
    .moreThan(0, 'Quote Value must be greater than 0')
    .required('Quote value is required'),
  refund: yup
    .number()
    .transform(numTransform)
    .nullable()
    .typeError('Refund must be a number')
    .min(0, 'Refund cannot be negative'),
  rebooking: yup.string().optional(),
});

const flightOptionSchemaActiveOrSkip = yup.lazy(value => {
  if (isFlightOptionInactive(value)) {
    return yup.mixed(); // inactive → skip validation entirely
  }
  return strictFlightOptionSchema; // active → full validation with field paths preserved
});

export const flightOptionsValidationSchema = yup.object({
  options: yup
    .array()
    .of(flightOptionSchemaActiveOrSkip) // inactive → skip; active → strict (field paths preserved)
    .min(1)
    .test(
      'first-flight-option-not-empty',
      'First flight option cannot be empty.',
      opts => {
        const first = opts?.[0];
        return !isFlightOptionInactive(first);
      },
    ),
});

export const flightOptionsInitialValues = [
  {
    quotes: [
      {
        fromCity: null,
        fromCityName: '',
        toCountry: null,
        toCountryName: null,
        toCity: null,
        date: '',
        deptTime: '',
        airline: null,
        airlineName: '',
        class: '',
        traveller: '',
      },
    ],
    agency: '',
    currency: getDefaultBaseCurrency(),
    terms: '',
    quoteValue: '',
    refund: '',
    rebooking: '',
  },
  {
    quotes: [
      {
        fromCity: null,
        fromCityName: '',
        toCountry: null,
        toCountryName: null,
        toCity: null,
        date: '',
        deptTime: '',
        airline: null,
        airlineName: '',
        class: '',
        traveller: '',
      },
    ],
    agency: '',
    currency: getDefaultBaseCurrency(),
    terms: '',
    quoteValue: '',
    refund: '',
    rebooking: '',
  },
  {
    quotes: [
      {
        fromCity: null,
        fromCityName: '',
        toCountry: null,
        toCountryName: null,
        toCity: null,
        date: '',
        deptTime: '',
        airline: null,
        airlineName: '',
        class: '',
        traveller: '',
      },
    ],
    agency: '',
    currency: getDefaultBaseCurrency(),
    terms: '',
    quoteValue: '',
    refund: '',
    rebooking: '',
  },
];

// Hotel Helper
const isEmptyQuote = q => {
  if (!q) return true;
  const {
    country,
    countryName,
    place,
    checkInDate,
    checkOutDate,
    hotelName,
    traveller,
  } = q;
  return (
    isEmptyObj(country) &&
    isEmptyObj(countryName) &&
    isEmptyObj(place) &&
    isEmptyObj(checkInDate) &&
    isEmptyObj(checkOutDate) &&
    isEmptyObj(hotelName) &&
    isEmptyObj(traveller)
  );
};

const isOptionInactive = opt => {
  const quotes = opt?.quotes ?? [];
  return Array.isArray(quotes) && quotes.every(isEmptyQuote);
};

const hotelQuoteSchema = yup.object({
  country: yup.object().required('Country is required'),
  countryName: yup.string().required('Country name is required'),
  place: yup.string().required('Place is required'),
  checkInDate: yup
    .string()
    .required('Check-in date is required')
    .test('not-in-past', 'Check-In Date cannot be in the past', value => {
      if (!value) return true;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return new Date(value) >= today;
    }),
  checkOutDate: yup
    .string()
    .required('Check-out date is required')
    .test(
      'is-after-checkin',
      'Check-Out Date cannot be before Check-In Date',
      function (value) {
        const { checkInDate } = this.parent;
        if (!checkInDate || !value) return true;
        return new Date(value) >= new Date(checkInDate);
      },
    )
    .test('not-in-past', 'Check-Out Date cannot be in the past', value => {
      if (!value) return true;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return new Date(value) >= today;
    }),
  hotelName: yup.string().optional(),
  traveller: yup.string().optional(),
});

const numberFromInput = s => {
  if (typeof s === 'string' && s.trim() === '') {
    return null;
  }
  if (typeof s === 'string') {
    return Number(s);
  }
  return s;
};

const strictHotelOptionSchema = yup.object({
  quotes: yup
    .array()
    .of(hotelQuoteSchema)
    .min(1, 'At least one quotation is required')
    .required(),
  currency: yup.string().required('Currency is required'),
  ratesPerNight: yup
    .number()
    .transform((v, orig) => numberFromInput(orig))
    .typeError('Rates per night must be a number')
    .moreThan(1, 'Rates per night must be greater than 0')
    .required('Rates per night is required'),
  quoteValue: yup
    .number()
    .transform((v, orig) => numberFromInput(orig))
    .typeError('Quote value must be a number')
    .moreThan(1, 'Quote Value must be greater than 0')
    .required('Quote value is required'),
  agency: yup.string().required('Travel Agency is required'),
  terms: yup.string().nullable(),
});

const optionSchemaActiveOrSkip = yup.lazy(value => {
  if (isOptionInactive(value)) {
    return yup.mixed(); // inactive: skip validation
  }
  return strictHotelOptionSchema; // active: validate normally (field-level errors preserved)
});

export const hotelOptionsValidationSchema = yup.object({
  options: yup
    .array()
    .of(optionSchemaActiveOrSkip) // lazy: skip inactive; strict for active
    .min(1)
    .test(
      'first-option-not-empty',
      'First hotel option cannot be empty.',
      opts => {
        const first = opts?.[0];
        return !isOptionInactive(first); // true = passes; false = show message above
      },
    ),
});

export const hotelOptionsInitialValues = [
  {
    quotes: [
      {
        country: '',
        countryName: '',
        place: '',
        checkInDate: '',
        checkOutDate: '',
        hotelName: '',
        traveller: '',
      },
    ],
    currency: '',
    ratesPerNight: null,
    quoteValue: null,
    terms: '',
    agency: '',
  },
  {
    quotes: [
      {
        country: '',
        countryName: '',
        place: '',
        checkInDate: '',
        checkOutDate: '',
        hotelName: '',
        traveller: '',
      },
    ],
    currency: '',
    ratesPerNight: null,
    quoteValue: null,
    terms: '',
    agency: '',
  },
  {
    quotes: [
      {
        country: '',
        countryName: '',
        place: '',
        checkInDate: '',
        checkOutDate: '',
        hotelName: '',
        traveller: '',
      },
    ],
    currency: '',
    ratesPerNight: null,
    quoteValue: null,
    terms: '',
    agency: '',
  },
];

// Other Consts

export const travelCategories = [
  'New',
  'Request for Quotation',
  'Pending for Decision',
  'Pending for Submission',
  'Pending for Approval',
  'Awaiting HR Approval',
  'Pending for Booking',
  'Booking Confirmed',
  'Booking Cancelled',
  'Request for Rebooking',
  'Request for Cancellation',
  'Rejected',
  'Travel Cancelled',
];

export const renderStatusColors = {
  New: {
    backgroundColor: '#2196F31A',
    fontColor: '#2196F3',
    borderColor: '#2196F3',
    dotColor: '#2196F3',
  },
  'Request for Quotation': {
    backgroundColor: '#0096881A',
    fontColor: '#009688',
    borderColor: '#009688',
    dotColor: '#009688',
  },
  'Pending for Decision': {
    backgroundColor: '#FF98001A',
    fontColor: '#B54708',
    borderColor: '#FF9800',
    dotColor: '#B54708',
  },
  'Pending for Submission': {
    backgroundColor: '#42A5F51A',
    fontColor: '#42A5F5',
    borderColor: '#42A5F5',
    dotColor: '#42A5F5',
  },
  'Pending for Approval': {
    backgroundColor: '#8882821A',
    fontColor: '#424242',
    borderColor: '#888282',
    dotColor: '#424242',
  },
  'Awaiting HR Approval': {
    backgroundColor: '#67A1241A',
    fontColor: '#67A124',
    borderColor: '#67A124',
    dotColor: '#67A124',
  },
  'Pending for Booking': {
    backgroundColor: '#3F51B51A',
    fontColor: '#3F51B5',
    borderColor: '#3F51B5',
    dotColor: '#3F51B5',
  },
  'Booking Confirmed': {
    backgroundColor: '#3EA9421A',
    fontColor: '#3EA942',
    borderColor: '#3EA942',
    dotColor: '#3EA942',
  },
  'Booking Cancelled': {
    backgroundColor: '#F443361A',
    fontColor: '#F44336',
    borderColor: '#F44336',
    dotColor: '#F44336',
  },
  'Request for Rebooking': {
    backgroundColor: '#9C27B01A',
    fontColor: '#9C27B0',
    borderColor: '#9C27B0',
    dotColor: '#9C27B0',
  },
  'Request for Cancellation': {
    backgroundColor: '#E91E631A',
    borderColor: '#E91E63',
    fontColor: '#E91E63',
    dotColor: '#E91E63',
  },
  Rejected: {
    backgroundColor: '#D32F2F1A',
    fontColor: '#D32F2F',
    borderColor: '#D32F2F',
    dotColor: '#D32F2F',
  },
  'Travel Cancelled': {
    backgroundColor: '#FF57221A',
    fontColor: '#FF5722',
    borderColor: '#FF5722',
    dotColor: '#FF5722',
  },
};
